import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

const PRIMARY = "#4f46e5";
const COLORS = [
  "#4f46e5",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#3b82f6",
  "#14b8a6",
  "#f97316",
  "#64748b",
];

document.addEventListener("DOMContentLoaded", () => {
  initSurveyStats();
  initFreitextSearch();
});

function initSurveyStats() {
  document.querySelectorAll("#survey-stats-container .survey-stat-card").forEach((card) => {
    const canvas = card.querySelector("canvas[data-type]");
    if (!canvas) return;

    const type = canvas.dataset.type;
    const rawLabels = canvas.dataset.labels ? canvas.dataset.labels.split(",") : [];
    const labels = rawLabels.map((s) => s.trim()).filter((s) => s.length > 0);
    const rawValues = canvas.dataset.values ? canvas.dataset.values.split(",") : [];
    const values = rawValues.map(Number);
    const total = values.reduce((a, b) => a + b, 0);

    // Update total count badge
    const totalBadge = card.querySelector(".stat-total-count");
    if (totalBadge) {
      totalBadge.textContent = `${total} Antwort${total === 1 ? "" : "en"}`;
    }

    // Calculate Average for Skala & Slider
    if (type === "skala" || type === "slider") {
      let weightedSum = 0;
      let validCount = 0;
      labels.forEach((lbl, idx) => {
        const num = parseFloat(lbl.replace(/[^0-9.-]/g, ""));
        const count = values[idx] || 0;
        if (!isNaN(num)) {
          weightedSum += num * count;
          validCount += count;
        }
      });

      if (validCount > 0) {
        const avg = (weightedSum / validCount).toFixed(1);
        const avgBadge = card.querySelector(".stat-average-badge");
        if (avgBadge) {
          avgBadge.classList.remove("d-none");
          avgBadge.innerHTML = `<i class="bi bi-calculator me-1"></i> Ø <strong>${avg}</strong>`;
        }
      }
    }

    // Render Option Breakdown Rows (Right Column)
    renderOptionBreakdown(card, labels, values, total, type);

    // Render Chart (Left Column)
    switch (type) {
      case "einfach":
      case "frage2":
      case "frage3":
      case "frage4":
        renderDoughnut(canvas, labels, values, total);
        break;
      case "mehrfach":
        renderHorizontalBar(canvas, labels, values, total);
        break;
      case "skala":
        renderVerticalBar(canvas, labels, values, total, "#f59e0b");
        break;
      case "slider":
        renderVerticalBar(canvas, labels, values, total, PRIMARY);
        break;
      default:
        renderHorizontalBar(canvas, labels, values, total);
        break;
    }
  });
}

function renderOptionBreakdown(card, labels, values, total, type) {
  const listEl = card.querySelector(".stat-breakdown-list");
  if (!listEl) return;

  listEl.innerHTML = "";
  if (!labels.length || total === 0) {
    listEl.innerHTML = '<p class="text-muted small mb-0">Keine Daten verfügbar</p>';
    return;
  }

  labels.forEach((label, idx) => {
    const val = values[idx] || 0;
    const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
    const color = type === "skala" ? "#f59e0b" : COLORS[idx % COLORS.length];

    const row = document.createElement("div");
    row.className = "mb-2 pb-2 border-bottom border-light-subtle";
    row.innerHTML = `
      <div class="d-flex justify-content-between align-items-center mb-1 small">
        <div class="d-flex align-items-center text-truncate me-2">
          <span class="d-inline-block rounded-circle me-2 flex-shrink-0" style="width: 10px; height: 10px; background-color: ${color};"></span>
          <span class="fw-semibold text-paper text-truncate" title="${label}">${label}</span>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="text-muted">${val} (${pct}%)</span>
        </div>
      </div>
      <div class="progress" style="height: 6px;">
        <div class="progress-bar rounded-pill" role="progressbar" style="width: ${pct}%; background-color: ${color};" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100"></div>
      </div>
    `;
    listEl.appendChild(row);
  });
}

function percentTooltip(total) {
  return {
    backgroundColor: "rgba(15, 23, 42, 0.9)",
    padding: 10,
    cornerRadius: 8,
    titleFont: { size: 13, weight: "bold" },
    bodyFont: { size: 12 },
    callbacks: {
      label: (ctx) => {
        const value = typeof ctx.parsed === "object" ? (ctx.parsed.y ?? ctx.parsed.x) : ctx.parsed;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        return `  ${value} Stimme(n) (${pct}%)`;
      },
    },
  };
}

function renderDoughnut(canvas, labels, values, total) {
  new Chart(canvas, {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: COLORS.slice(0, labels.length),
          borderWidth: 3,
          borderColor: "#ffffff",
          hoverOffset: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "65%",
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            usePointStyle: true,
            padding: 15,
            font: { size: 12, family: "Inter, sans-serif" },
          },
        },
        tooltip: percentTooltip(total),
      },
    },
  });
}

function renderHorizontalBar(canvas, labels, values, total) {
  new Chart(canvas, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: COLORS.slice(0, labels.length),
          borderRadius: 8,
          maxBarThickness: 24,
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: percentTooltip(total),
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 11 } },
          grid: { color: "#f1f5f9" },
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 12, weight: "500" } },
        },
      },
    },
  });
}

function renderVerticalBar(canvas, labels, values, total, color = PRIMARY) {
  new Chart(canvas, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: color,
          borderRadius: { topLeft: 8, topRight: 8 },
          maxBarThickness: 42,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: percentTooltip(total),
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 11 } },
          grid: { color: "#f1f5f9" },
        },
        x: {
          grid: { display: false },
          ticks: { font: { size: 12, weight: "500" } },
        },
      },
    },
  });
}

function initFreitextSearch() {
  document.querySelectorAll(".freitext-search-input").forEach((input) => {
    input.addEventListener("input", (e) => {
      const term = e.target.value.toLowerCase().trim();
      const card = input.closest(".survey-stat-card");
      if (!card) return;

      const items = card.querySelectorAll(".freitext-item");
      let visibleCount = 0;
      items.forEach((item) => {
        const text = item.textContent.toLowerCase();
        const matches = text.includes(term);
        item.style.display = matches ? "" : "none";
        if (matches) visibleCount++;
      });

      const emptyEl = card.querySelector(".freitext-empty-search");
      if (emptyEl) {
        emptyEl.classList.toggle("d-none", visibleCount > 0);
      }
    });
  });
}
